/**
 * OrgPickerSheet — 0zzz-fe: in-place "choose organization" picker.
 *
 * Mounted once, app-wide (App.tsx, alongside ToSReacceptanceModal, inside
 * OrganizationProvider). Listens for ORG_PICKER_REQUIRED_EVENT, dispatched by
 * services/api/client.ts's response interceptor when a write is refused with
 * 403 ORG_HEADER_INVALID (the stored org is stale) or 403
 * ORG_SELECTION_REQUIRED (no org selected, 2+ memberships). Calls
 * preventDefault() on that event so the client does NOT fall back to its
 * legacy clear-localStorage-and-hard-redirect behavior — see client.ts's
 * dispatch site and orgPickerEvents.ts for why leaving the stale org in
 * localStorage while this is open matters (fail-closed background writes).
 *
 * CRITICAL fix (post-review, 2026-09-23): this component fetches its org
 * list via a DIRECT organizationAPI.getAll() call — deliberately NOT
 * useOrganization().refreshOrganizations() and NOT
 * queryKeys.organizations.contextList(). Both carry OrganizationContext's own
 * reconcile side effects (OrganizationContext.tsx's dataUpdatedAt effect,
 * ~225-253): if the stored org isn't in the fresh list — always true for a
 * stale org — it clears localStorage itself, and if only one org remains
 * with none "selected" it auto-selects it. Either path would wipe/replace
 * localStorage the instant this picker opened, before the user chose
 * anything — exactly the misroute 0zzz-ev exists to prevent, just triggered
 * by this component instead of client.ts. The only things allowed to touch
 * `selectedOrganization` while this is open are the user's own explicit
 * choices in handlePick/handleLeave below.
 *
 * Also sets services/api/client.ts's module-level "org selection pending"
 * flag for the same reason, as defence in depth: a background write firing
 * in the window between the picker opening and the user choosing would still
 * carry the (deliberately-preserved) stale X-Organization-Id header and get
 * refused server-side, which is harmless but noisy — the client-side lock
 * short-circuits it locally instead, with safety routes exempted so a
 * safeword or logout is never blockable by this.
 *
 * Deliberately NOT dismissible by backdrop tap or Escape (Sheet/Modal
 * `dismissible={false}` + `hideCloseButton`) — the only exits are picking an
 * org, or the explicit "Go to Organizations instead" confirm flow (available
 * from every phase, including while loading, so a hung fetch can never trap
 * the user under a non-dismissible overlay — backed by a 15s load timeout
 * that moves loading -> error on its own). Picking an org never auto-resends
 * the write that failed; it shows a persistent note instead, cleared on
 * manual dismiss or on navigating away from the page it appeared on (NOT on
 * the next successful write anywhere — Helm has several automatic background
 * writers, e.g. presence/heartbeat, read receipts, mark-read, analytics,
 * that could otherwise wipe it before the user ever saw it).
 */
import { extractOrganizationList } from '../utils/organizationList';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet } from './ui/Sheet';
import { Button } from './ui/Button';
import { useOrganization, Organization } from '../contexts/OrganizationContext';
import { useTerminology } from '../hooks/useTerminology';
import { isDomRole, isSubRole } from '../utils/roleHelpers';
import { organizationAPI } from '../services/api';
import { ORG_PICKER_REQUIRED_EVENT, OrgPickerRequiredEventDetail } from '../utils/orgPickerEvents';
import {
  setOrgSelectionPending,
  markOrgAbandoned,
  resetAbandonedOrgIds,
  AUTH_ERROR_EVENT,
  AUTH_LOGIN_EVENT,
} from '../services/api/client';

type Phase = 'loading' | 'error' | 'ready';

// A hung fetch must not trap the user under a non-dismissible overlay
// forever — 15s with no response moves loading -> error (Retry / Go to
// Organizations instead are both still reachable at that point).
const LOAD_TIMEOUT_MS = 15000;

function CloseXIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const OrgPickerSheet: React.FC = () => {
  const { selectOrganization, clearOrganization } = useOrganization();
  const { t } = useTerminology();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState<OrgPickerRequiredEventDetail['reason'] | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Refs so the (mount-once) event listener and the async load() call always
  // see the latest values without re-registering the listener every render.
  const isOpenRef = useRef(false);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  const staleOrgIdRef = useRef<string | undefined>(undefined);
  const loadGenRef = useRef(0);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The pathname the post-pick note appeared on — used to clear it on
  // navigation away (see note in the file header on why not "any write").
  const noteOriginPathRef = useRef<string | null>(null);

  const clearLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current !== null) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);
  useEffect(() => clearLoadTimeout, [clearLoadTimeout]);

  // Fetches the org list DIRECTLY against the API. See the file header for
  // why this must never be useOrganization().refreshOrganizations() or the
  // shared queryKeys.organizations.contextList() cache.
  const load = useCallback(() => {
    const gen = ++loadGenRef.current;
    clearLoadTimeout();
    setPhase('loading');

    loadTimeoutRef.current = setTimeout(() => {
      if (loadGenRef.current !== gen) return;
      setPhase('error');
    }, LOAD_TIMEOUT_MS);

    organizationAPI.getAll().then((response) => {
      if (loadGenRef.current !== gen) return; // superseded by a retry or timeout
      clearLoadTimeout();
      // A 200 without a real array throws here and lands in .catch -> the error phase (never "no organization").
      const fresh = extractOrganizationList<Organization>(response.data);
      const staleId = staleOrgIdRef.current;
      setOrgs(staleId ? fresh.filter((o) => o.id !== staleId) : fresh);
      setPhase('ready');
    }).catch(() => {
      if (loadGenRef.current !== gen) return;
      clearLoadTimeout();
      setPhase('error');
    });
  }, [clearLoadTimeout]);

  useEffect(() => {
    function handleRequired(e: Event) {
      const ce = e as CustomEvent<OrgPickerRequiredEventDetail>;
      // Claim it unconditionally (even if we're ignoring it below because
      // we're already open) — the client must never fall back to its
      // legacy clear+redirect behavior while this component is mounted.
      e.preventDefault();
      if (isOpenRef.current) return; // single instance — already open, ignore
      // Set synchronously (not only via the isOpen-driven effect above) so a
      // SECOND event dispatched in the same tick — before React has
      // re-rendered and that effect has run — still sees this as claimed,
      // not a fresh open.
      isOpenRef.current = true;
      setOrgSelectionPending(true);
      staleOrgIdRef.current = ce.detail?.staleOrgId;
      setReason(ce.detail?.reason ?? null);
      setShowLeaveConfirm(false);
      setIsOpen(true);
    }
    window.addEventListener(ORG_PICKER_REQUIRED_EVENT, handleRequired);
    return () => window.removeEventListener(ORG_PICKER_REQUIRED_EVENT, handleRequired);
  }, []);

  // Session expiry or a new login underneath the picker: close it and release
  // the write lock, so the next session never inherits a stuck lock or an org
  // list fetched for the previous user. (The picker can't be dismissed, so the
  // logout menu is unreachable while it's open — these are the only paths.)
  // Unmount also releases it: the lock is module state and outlives this component.
  useEffect(() => {
    function reset() {
      loadGenRef.current++;
      clearLoadTimeout();
      isOpenRef.current = false;
      resetAbandonedOrgIds(); // they belonged to the previous session
      // Never let the next session's first paint show this session's org list.
      setOrgs([]);
      setPhase('loading');
      setOrgSelectionPending(false);
      setIsOpen(false);
      setShowLeaveConfirm(false);
      setNote(null);
    }
    window.addEventListener(AUTH_ERROR_EVENT, reset);
    window.addEventListener(AUTH_LOGIN_EVENT, reset);
    return () => {
      window.removeEventListener(AUTH_ERROR_EVENT, reset);
      window.removeEventListener(AUTH_LOGIN_EVENT, reset);
      setOrgSelectionPending(false);
    };
  }, [clearLoadTimeout]);

  useEffect(() => {
    if (isOpen) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Clear the persistent note when the user navigates away from the page it
  // was shown on. Deliberately NOT wired to "the next successful write
  // anywhere" — see file header.
  useEffect(() => {
    if (note && noteOriginPathRef.current !== null && location.pathname !== noteOriginPathRef.current) {
      setNote(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handlePick = useCallback((org: Organization) => {
    // Queries still mounted for the org being left refetch once below and 403;
    // the client must treat those as leftovers, not a new stale-org event.
    markOrgAbandoned(staleOrgIdRef.current);
    selectOrganization(org);
    // Deliberately NOT re-sending the write that triggered this — the user
    // must re-confirm intent against the newly-selected org, not have it
    // silently resent into whichever org they just picked.
    queryClient.invalidateQueries();
    setOrgSelectionPending(false);
    isOpenRef.current = false; // synchronously, so a refusal in the next frame isn't swallowed
    setIsOpen(false);
    setShowLeaveConfirm(false);
    noteOriginPathRef.current = location.pathname;
    setNote(`Now in ${org.name}. Review your changes and save again.`);
  }, [selectOrganization, queryClient, location.pathname]);

  const handleLeave = useCallback(() => {
    // Same as handlePick: a queued write still carrying the old org must not
    // re-open the picker at the user who just chose to leave it.
    markOrgAbandoned(staleOrgIdRef.current);
    clearOrganization();
    setOrgSelectionPending(false);
    isOpenRef.current = false;
    setIsOpen(false);
    setShowLeaveConfirm(false);
    navigate('/organizations');
  }, [clearOrganization, navigate]);

  // CEO decision 2026-10-12 (0zzz-fe blocker 3): safeword is reachable from the picker (not in the no-org phase, 0zzz-uhs). The picker is
  // app-wide and non-dismissible, so following the link must close it, or its backdrop would cover the safeword
  // page. Deliberately keeps the stored org and the note: nothing was picked, nothing is abandoned (the safeword
  // routes need no org header). The write lock is released so the safeword page can make its own calls.
  const handleSafeword = useCallback(() => {
    loadGenRef.current++;
    clearLoadTimeout();
    setOrgSelectionPending(false);
    isOpenRef.current = false;
    setIsOpen(false);
    setShowLeaveConfirm(false);
  }, [clearLoadTimeout]);

  if (!isOpen && !note) return null;

  const zeroOrgs = phase === 'ready' && orgs.length === 0;
  const oneOrgLeft = phase === 'ready' && orgs.length === 1;

  let heading = 'Which organization is this for?';
  let subtext = "You're part of more than one — pick one to continue.";
  if (reason === 'stale') {
    heading = 'You no longer have access to your previous organization.';
    subtext = oneOrgLeft
      ? 'This is your only remaining organization — confirm to continue.'
      : "Choose which organization this is for, and we'll pick up right where you left off.";
  } else if (oneOrgLeft) {
    subtext = 'This is your only remaining organization — confirm to continue.';
  }

  const goToOrgsInsteadLink = (
    <Button
      type="button"
      variant="text"
      size="sm"
      className="self-start underline mt-1"
      onClick={() => setShowLeaveConfirm(true)}
    >
      Go to Organizations instead
    </Button>
  );

  return (
    <>
      {isOpen && (
        <Sheet open={isOpen} onDismiss={() => {}} dismissible={false} hideCloseButton ariaLabel="Choose an organization">
          <div className="org-picker-sheet flex flex-col gap-3">
            {phase === 'loading' && (
              <>
                <h3 className="text-lg font-bold text-text-primary mb-0">{heading}</h3>
                <p className="text-sm text-text-secondary mb-1">Loading your organizations…</p>
                <div className="flex flex-col gap-2" aria-hidden="true">
                  <div className="h-16 rounded-md animate-pulse" style={{ background: 'var(--theme-bg-tertiary)' }} />
                  <div className="h-16 rounded-md animate-pulse" style={{ background: 'var(--theme-bg-tertiary)' }} />
                </div>
                {/* Safety review: never trap the user under a non-dismissible
                    overlay with no exit while a fetch is pending. */}
                {goToOrgsInsteadLink}
              </>
            )}

            {phase === 'error' && (
              <>
                <h3 className="text-lg font-bold text-text-primary mb-0">Couldn&rsquo;t load your organizations.</h3>
                <p className="text-sm text-text-secondary mb-1">Check your connection and try again.</p>
                <Button type="button" variant="primary" fullWidth onClick={load}>
                  Retry
                </Button>
                {goToOrgsInsteadLink}
              </>
            )}

            {phase === 'ready' && zeroOrgs && (
              <>
                <h3 className="text-lg font-bold text-text-primary mb-0">You&rsquo;re not part of any organization right now.</h3>
                <p className="text-sm text-text-secondary mb-1">
                  Join or create one to keep going. There&rsquo;s nothing to pick from here.
                </p>
                <Button type="button" variant="primary" fullWidth onClick={handleLeave}>
                  Go to Organizations
                </Button>
              </>
            )}

            {phase === 'ready' && !zeroOrgs && (
              <>
                <h3 className="text-lg font-bold text-text-primary mb-0">{heading}</h3>
                <p className="text-sm text-text-secondary mb-1">{subtext}</p>
                {/* Bounded scroll area so the exit link below stays on screen however
                    many orgs there are (staging Chrome pass: 111 orgs pushed it ~8000px down). */}
                <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto overscroll-contain" data-testid="org-picker-list">
                  {orgs.map((org) => {
                    const isDom = isDomRole(org.role);
                    const isSub = isSubRole(org.role);
                    // Anything neither Dom- nor Sub-equivalent (a future role)
                    // gets a neutral label instead of being disclosed as a Sub role.
                    const roleLabel = isDom ? t('dominant') : isSub ? t('submissive') : 'Member';
                    return (
                      <Button
                        key={org.id}
                        type="button"
                        variant="ghost"
                        fullWidth
                        data-testid={`org-picker-row-${org.id}`}
                        onClick={() => handlePick(org)}
                        className={`h-auto min-h-[64px] py-2.5 justify-between text-left rounded-lg gap-3 border ${
                          oneOrgLeft
                            ? 'bg-[var(--theme-primary-light)] border-[var(--theme-border-focus)]'
                            : 'bg-[var(--theme-card-bg)] border-[var(--theme-card-border)]'
                        }`}
                      >
                        <span className="flex items-center gap-3 min-w-0 flex-1">
                          <span
                            aria-hidden="true"
                            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                            style={{ background: 'var(--theme-bg-tertiary)', color: 'var(--theme-text-secondary)' }}
                          >
                            {org.name.charAt(0).toUpperCase()}
                          </span>
                          <span className="min-w-0 flex-1 flex flex-col items-start">
                            <span className="text-[15px] font-semibold text-text-primary truncate max-w-full" title={org.name}>
                              {org.name}
                            </span>
                            {oneOrgLeft && (
                              <span className="text-xs text-text-tertiary">Only remaining organization</span>
                            )}
                          </span>
                        </span>
                        <span
                          className={`text-xs font-semibold shrink-0 ${
                            isDom
                              ? 'text-[var(--theme-role-dom)]'
                              : isSub
                                ? 'text-[var(--theme-role-sub)]'
                                : 'text-text-secondary'
                          }`}
                        >
                          {roleLabel}
                        </span>
                      </Button>
                    );
                  })}
                </div>
                {goToOrgsInsteadLink}
              </>
            )}

            {/* Present in EVERY phase: a safety exit is never behind an org choice. With no organization left the page
                shows an honest dead end with crisis resources (0zzz-uhy, CEO 2026-10-13), so the link is offered there too. */}
            <Link
              to="/safeword"
              onClick={handleSafeword}
              data-testid="org-picker-safeword-link"
              className="self-start inline-flex items-center min-h-[44px] px-1 text-sm font-medium underline text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-primary)]"
            >
              Safeword
            </Link>

            {showLeaveConfirm && (
              <div
                role="alertdialog"
                aria-label="Leaving discards unsaved changes"
                className="rounded-md px-3 py-3 text-xs"
                style={{ background: 'var(--theme-error-bg)', border: '1px solid var(--theme-error-border)', color: 'var(--theme-text-primary)' }}
              >
                Unsaved changes will be lost.
                <div className="flex gap-2 mt-2">
                  <Button type="button" variant="danger" size="sm" onClick={handleLeave}>
                    Leave without saving
                  </Button>
                  <Button type="button" variant="secondary" size="sm" onClick={() => setShowLeaveConfirm(false)}>
                    Stay here
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Sheet>
      )}

      {note && (
        <div
          role="status"
          className="org-picker-note fixed left-1/2 z-[210] flex items-start gap-2 rounded-md px-4 py-3 text-sm shadow-lg"
          style={{
            bottom: 'calc(88px + env(safe-area-inset-bottom))',
            width: 'min(92vw, 420px)',
            transform: 'translateX(-50%)',
            background: 'var(--theme-primary-light)',
            border: '1px solid var(--theme-border-focus)',
            color: 'var(--theme-text-primary)',
          }}
        >
          <span className="flex-1">{note}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Dismiss notice"
            className="!h-6 !min-h-0 !w-6 !p-0 shrink-0"
            onClick={() => setNote(null)}
          >
            <CloseXIcon />
          </Button>
        </div>
      )}
    </>
  );
};

export default OrgPickerSheet;
